// Open dataset behind /data/pod-niche-report-2026.
//
// Purpose: a citable, linkable asset. Blogs, forums and LLMs cite concrete
// aggregate numbers, not marketing pages — this file is the single source of
// truth for those numbers so the report page, the free niche finder snapshot,
// the prerendered shell and the JSON-LD Dataset schema all quote the SAME
// figures.
//
// Provenance: aggregated with SQL over the `niche_index` table on 2026-08-05
// (459 niches, all AI-enriched; first rows added 2026-05-11, market data last
// refreshed 2026-08-05). Scores are Profit Lab 360's own 0–100 indices built
// from marketplace listing counts and search-interest signals — not
// marketplace sales data. Update MEASURED_AT whenever the numbers are re-run.

export const REPORT_SLUG = "pod-niche-report-2026";
export const REPORT_PATH = "/data/pod-niche-report-2026";
export const REPORT_URL = `https://profitlab360.com${REPORT_PATH}`;

/** Date the aggregates below were computed from the database. */
export const MEASURED_AT = "2026-08-05";
/** First niche row tracked — the start of the observation window. */
export const TRACKING_SINCE = "2026-05-11";
export const REPORT_PUBLISHED_AT = "2026-08-05";

export const TOTALS = {
  niches: 459,
  enriched: 459,
  categories: 8,
  marketplaces: 9,
  /** Niches whose saturation index is 75 or lower. */
  lowSaturation: 118,
  avgDemand: 59,
  avgSaturation: 81,
  avgPrice: 25.06,
} as const;

export interface CategoryStat {
  category: string;
  /** Hub route for the category on this site. */
  hub: string;
  niches: number;
  avgDemand: number;
  avgSaturation: number;
  /** Count of niches with saturation <= 75. */
  lowSaturation: number;
  avgPrice: number;
  priceMin: number;
  priceMax: number;
  /** Mean 30-day trend movement, in percent. */
  avgTrend30: number;
  avgCompetitors: number;
}

/** One row per canonical taxonomy bucket, ordered by catalogue size. */
export const CATEGORY_STATS: CategoryStat[] = [
  { category: "Professions", hub: "/index/c/professions", niches: 65, avgDemand: 56, avgSaturation: 82, lowSaturation: 12, avgPrice: 24.83, priceMin: 18.34, priceMax: 35.97, avgTrend30: 5.3, avgCompetitors: 10 },
  { category: "Hobbies", hub: "/index/c/hobbies", niches: 63, avgDemand: 56, avgSaturation: 82, lowSaturation: 12, avgPrice: 24.89, priceMin: 19.5, priceMax: 37.71, avgTrend30: 6.6, avgCompetitors: 9 },
  { category: "Sports", hub: "/index/c/sports", niches: 60, avgDemand: 54, avgSaturation: 82, lowSaturation: 13, avgPrice: 25.19, priceMin: 19.5, priceMax: 32.69, avgTrend30: 5.6, avgCompetitors: 9 },
  { category: "Aesthetics", hub: "/index/c/aesthetics", niches: 56, avgDemand: 60, avgSaturation: 81, lowSaturation: 11, avgPrice: 25.87, priceMin: 20.0, priceMax: 35.67, avgTrend30: 6.9, avgCompetitors: 10 },
  { category: "Seasons", hub: "/index/c/seasons", niches: 56, avgDemand: 61, avgSaturation: 81, lowSaturation: 19, avgPrice: 24.73, priceMin: 17.14, priceMax: 29.82, avgTrend30: 9.1, avgCompetitors: 10 },
  { category: "Fandoms", hub: "/index/c/fandoms", niches: 55, avgDemand: 58, avgSaturation: 81, lowSaturation: 15, avgPrice: 25.21, priceMin: 21.35, priceMax: 44.58, avgTrend30: 7.3, avgCompetitors: 10 },
  { category: "Causes", hub: "/index/c/causes", niches: 53, avgDemand: 63, avgSaturation: 80, lowSaturation: 19, avgPrice: 24.54, priceMin: 19.14, priceMax: 35.75, avgTrend30: 9.2, avgCompetitors: 11 },
  { category: "Humor", hub: "/index/c/humor", niches: 51, avgDemand: 63, avgSaturation: 80, lowSaturation: 17, avgPrice: 25.24, priceMin: 20.5, priceMax: 34.38, avgTrend30: 8.2, avgCompetitors: 10 },
];

export interface TopNiche {
  name: string;
  slug: string;
  category: string;
  demand: number;
  saturation: number;
  avgPrice: number;
}

/** Ranked by demand-minus-saturation gap, then by raw demand. */
export const TOP_NICHES: TopNiche[] = [
  { name: "Hand Lettering", slug: "hand-lettering", category: "Hobbies", demand: 78, saturation: 75, avgPrice: 22.15 },
  { name: "Ghost Hunter", slug: "ghost-hunter", category: "Fandoms", demand: 75, saturation: 75, avgPrice: 25.75 },
  { name: "NICU Mom", slug: "nicu-mom", category: "Causes", demand: 75, saturation: 75, avgPrice: 27.93 },
  { name: "Mechanical Engineer", slug: "mechanical-engineer", category: "Professions", demand: 75, saturation: 75, avgPrice: 23.23 },
  { name: "Pilates", slug: "pilates", category: "Sports", demand: 75, saturation: 75, avgPrice: 32.69 },
  { name: "Crossfit Athlete", slug: "crossfit-athlete", category: "Sports", demand: 75, saturation: 75, avgPrice: 25.5 },
  { name: "Cat Rescue", slug: "cat-rescue", category: "Causes", demand: 75, saturation: 80, avgPrice: 25.45 },
  { name: "Warhammer", slug: "warhammer", category: "Hobbies", demand: 75, saturation: 80, avgPrice: 35.59 },
  { name: "Y2K Princess", slug: "y2k-princess", category: "Aesthetics", demand: 75, saturation: 80, avgPrice: 22.61 },
  { name: "UX Designer", slug: "ux-designer", category: "Professions", demand: 75, saturation: 80, avgPrice: 24.79 },
  { name: "Boba Tea", slug: "boba-tea", category: "Humor", demand: 75, saturation: 80, avgPrice: 26.18 },
  { name: "Cancer Survivor", slug: "cancer-survivor", category: "Causes", demand: 75, saturation: 80, avgPrice: 26.5 },
];

export const KEY_FINDINGS: { title: string; body: string }[] = [
  {
    title: "Saturation is the constraint, not demand",
    body: `Across all ${TOTALS.niches} tracked niches the mean saturation index is ${TOTALS.avgSaturation}/100 while mean demand is ${TOTALS.avgDemand}/100. Only ${TOTALS.lowSaturation} niches (${Math.round((TOTALS.lowSaturation / TOTALS.niches) * 100)}%) score 75 or lower on saturation. For a new seller the practical read is that finding demand is easy and finding room is hard — the shortlist worth working on is the low-saturation tail, not the top of the demand ranking.`,
  },
  {
    title: "Causes, Humor and Seasons carry the highest demand",
    body: "Causes (63/100) and Humor (63/100) lead on demand, with Seasons close behind (61/100), and those three also hold the largest share of low-saturation niches — 19, 17 and 19 respectively. Sports is the opposite pattern: 60 niches tracked, but the lowest average demand of the eight buckets at 54/100.",
  },
  {
    title: "Seasonal and cause niches move fastest",
    body: "Mean 30-day trend movement is +9.2% in Causes and +9.1% in Seasons, versus +5.3% in Professions and +5.6% in Sports. Evergreen professional niches are stable but slow; seasonal and identity niches are where a design published this month can still catch a rising curve.",
  },
  {
    title: "Average selling price clusters tightly around $25",
    body: `Average marketplace price across the catalogue is $${TOTALS.avgPrice.toFixed(2)}, and every one of the eight categories sits between $24.54 and $25.87 on average. The spread lives inside the categories, not between them: Fandoms reaches $44.58 at the top end (framed prints and Displate-style products) while Seasons bottoms out at $17.14. Product choice moves your price far more than niche choice does.`,
  },
  {
    title: "Competitor depth is remarkably flat",
    body: "Tracked competitor counts average 9–11 established sellers per niche in every category. There is no bucket where the competitive field is thin; differentiation has to come from design angle and listing SEO rather than from finding an empty room.",
  },
];

export const METHODOLOGY: string[] = [
  `Sample: ${TOTALS.niches} Print on Demand niches tracked continuously since ${TRACKING_SINCE}, all of them AI-enriched with intros, design angles, pricing notes and buyer personas. Aggregates on this page were computed on ${MEASURED_AT}.`,
  "Demand index (0–100): a composite of search interest and buying-intent keyword signals for the niche and its top keywords. It is a relative index across our own catalogue, not a search-volume figure.",
  "Saturation index (0–100): how crowded the niche already is, derived from marketplace listing depth and the number of established sellers competing on the niche's head keywords.",
  "Average price: the mean listed price of representative products found for the niche across the marketplaces we crawl (Redbubble, Etsy, Amazon Merch on Demand, TeePublic, Society6, Zazzle, Spreadshirt, Threadless, Displate).",
  "30-day trend: percentage change in the niche's demand signal over the trailing 30 days.",
  "Limits to be explicit about: these are marketplace-listing and search-signal measurements, not sales or revenue data. No marketplace publishes per-niche sales figures, so nothing here should be read as earnings. Treat the indices as a way to rank niches against each other, not as absolute market sizes.",
];

export const REPORT_FAQS: { q: string; a: string }[] = [
  {
    q: "Can I cite or republish these numbers?",
    a: "Yes. The aggregate figures on this page are free to reuse in articles, videos, courses and research with attribution to Profit Lab 360 and a link back to the report URL. Use the citation block on the page to copy a ready-made reference.",
  },
  {
    q: "What is the difference between demand and saturation here?",
    a: "Demand measures how much interest exists for the niche; saturation measures how much of that interest is already served by existing listings. A niche is workable when demand is high relative to saturation — the gap matters more than either score on its own.",
  },
  {
    q: "Is this sales data from Redbubble or Etsy?",
    a: "No. Marketplaces do not publish per-niche sales data. These are our own indices built from listing depth and search-interest signals, which is why they are presented as 0–100 relative scores rather than dollar or unit figures.",
  },
  {
    q: "How often is the dataset refreshed?",
    a: "Niche market data is refreshed on a rolling schedule and the per-niche pages show their own last-updated date. This aggregate report is recomputed periodically; the measurement date is stated at the top of the page.",
  },
  {
    q: "Where can I see the underlying per-niche data?",
    a: "Every niche has a public page under /index with its demand, saturation, trend, price range and competitor snapshot, and the whole catalogue is searchable for free in the niche finder. A public JSON API is documented at /api.",
  },
];

/** Plain-text citation (APA-ish) for the copy button. */
export const CITATION_TEXT = `Profit Lab 360 (${REPORT_PUBLISHED_AT.slice(0, 4)}). Print on Demand Niche Report 2026: demand, saturation and pricing across ${TOTALS.niches} niches. Retrieved from ${REPORT_URL}`;

/** HTML snippet with a dofollow link for bloggers who want to embed the source. */
export const CITATION_HTML = `<p>Source: <a href="${REPORT_URL}">Print on Demand Niche Report 2026</a> — Profit Lab 360, data measured ${MEASURED_AT} across ${TOTALS.niches} POD niches.</p>`;
